// Ordering helpers shared by the board's drag handlers, cache updates and
// page-level derivations. Cards and columns are both ordered by an ascending
// `position` field, and `.slice().sort((a, b) => a.position - b.position)` was
// previously open-coded in roughly ten places.

interface Positioned {
  _id: string;
  position: number;
}

interface WithCards {
  _id: string;
  cards: readonly { _id: string }[];
}

/** Ascending `position` comparator. */
export const byPosition = (a: Positioned, b: Positioned) =>
  a.position - b.position;

/** Non-mutating sort by ascending `position`. */
export function sortedByPosition<T extends Positioned>(
  items: readonly T[]
): T[] {
  return items.slice().sort(byPosition);
}

/**
 * Id of the highest-position card, or undefined when empty. Used as the
 * `afterCardId` anchor when appending to a column's tail. A single linear
 * scan — the old implementation sorted the whole array to read its last item.
 */
export function tailCardId<T extends Positioned>(
  cards: readonly T[]
): string | undefined {
  let tail: T | undefined;
  for (const card of cards) {
    if (!tail || card.position > tail.position) tail = card;
  }
  return tail?._id;
}

/** The column currently holding `cardId`, or undefined if no column does. */
export function columnOfCard<T extends WithCards>(
  columns: readonly T[],
  cardId: string
): T | undefined {
  return columns.find((c) => c.cards.some((k) => k._id === cardId));
}
